import {
    getTokenRequestHeaders,
    qidigoAuthenticatedFetch,
    qidigoFetch,
    RequestAdditionalParams
} from "./qidigoFetch";
import User from "../dtos/User";
import { getToken, setToken } from "./auth";
import Group from "../dtos/Group";
import FamilyMember from "../dtos/FamilyMember";
import Offer from "../dtos/Offer";
import Wish from "../dtos/Wish";
import BasicPostResponse from "../dtos/BasicPostResponse";
import UserCompletionState from "./types/UserCompletionState";
import { hasCompletedRegistration } from "../utils/qidigo";
import CartItem from "../dtos/CartItem";
import { addItem, removeItem, setItems } from "./localStorageShoppingCart";
import { IRelatedOffer } from "../views/wishlist/types";
import Membership from "../dtos/Membership";
import { IActivity, ICalendarGroup } from "../views/calendar/types";
import { trackEvent } from "./tracker";
import { IRestrictionByMembership } from "../types";
import { ILocation, IRentableUnavailability } from "../views/RentableUnavailability/types";
import { v4 as uuidv4 } from 'uuid';
import environment from "../environment";

const GENERIC_LOGIN_ERROR_TRANSLATION_KEY = 'login.errors.generic';
const INVALID_USER_TRANSLATION_KEY = 'login.errors.userNotFound';
const QIDIGO_GUEST_TOKEN = 'qidigo_guest';
export const INVALID_PASSWORD_TRANSLATION_KEY = 'login.errors.wrongPassword';

export const login = async (email: string, password: string, additionalParams?: RequestAdditionalParams): Promise<string> => {
    try {
        const init = {
            method: 'POST',
            body: JSON.stringify({ auth: { email, password } })
        };
        const response = await qidigoFetch("authenticate", init, additionalParams);
        const { jwt } = response as any;
        setToken(jwt);
        if (additionalParams?.redirectToRegisterWithCompletionState) {
            const completionState = await getUserCompletionState();
            if (!hasCompletedRegistration(completionState)) {
                additionalParams.redirectToRegisterWithCompletionState?.(completionState, additionalParams.search)
            } else if (Boolean(additionalParams.goToRedirectUrl)) {
                additionalParams.goToRedirectUrl?.()
            } else if (Boolean(additionalParams.redirectToGroups)) {
                additionalParams.redirectToGroups?.()
            }
        }
        return jwt;
    } catch (e) {
        let errorMessageKey = GENERIC_LOGIN_ERROR_TRANSLATION_KEY;
        if (e.status === 404) {
            errorMessageKey = INVALID_USER_TRANSLATION_KEY;
        } else if (e.status === 403) {
            errorMessageKey = INVALID_PASSWORD_TRANSLATION_KEY;
        }
        // eslint-disable-next-line no-throw-literal
        throw { ...e, messageKey: errorMessageKey };
    }
};

interface RegisterUser {
    firstName: string,
    lastName: string,
    birthday: string,
    sex: string,
    email: string,
    password: string,
}

export const register = async (user: RegisterUser, additionalParams?: RequestAdditionalParams) => {
    const usersInit = {
        method: 'POST',
        body: JSON.stringify({
            user: {
                first_name: user.firstName,
                last_name: user.lastName,
                birthday: user.birthday,
                sex: user.sex,
                email: user.email,
                password_string: user.password,
            }
        })
    };
    const response = await qidigoFetch(`users`, usersInit, additionalParams);
    const { jwt: token } = response as any;
    setToken(token);
    return await getCurrentUser();
};

interface PatchUser {
    firstName: string,
    lastName: string,
    birthday: string,
    sex: string
}

export const patchUser = async (userId: number, user: PatchUser, additionalParams?: RequestAdditionalParams) => {
    const usersInit = {
        method: 'PATCH',
        body: JSON.stringify({
            user: {
                first_name: user.firstName,
                last_name: user.lastName,
                birthday: user.birthday,
                sex: user.sex
            }
        })
    };
    await qidigoAuthenticatedFetch(`users/${userId}`, usersInit, additionalParams);
    return await getCurrentUser();
};

export const connectWithFacebook = async (facebookCodeUrlParam: string, additionalParams?: RequestAdditionalParams): Promise<string> => {
    const response = await qidigoFetch(`authenticate/facebook/callback${facebookCodeUrlParam}&redirect_url=${encodeURIComponent(window.location.origin + '/embed/v1.0/authentication/facebook/return')}`, { method: 'GET' });
    const { jwt } = response as any;
    setToken(jwt);
    if (additionalParams?.redirectToRegisterWithCompletionState) {
        const completionState = await getUserCompletionState();
        if (!hasCompletedRegistration(completionState)) {
            additionalParams.redirectToRegisterWithCompletionState(completionState, additionalParams.search)
        } else if (Boolean(additionalParams.goToRedirectUrl)) {
            additionalParams.goToRedirectUrl?.()
        } else if (Boolean(additionalParams.redirectToGroups)) {
            additionalParams.redirectToGroups?.()
        }
    }
    return jwt;
};

export const connectWithGoogle = async (googleCodeUrlParam: string, additionalParams?: RequestAdditionalParams): Promise<string> => {
    const response = await qidigoFetch(`authenticate/google/callback${googleCodeUrlParam}&redirect_url=${encodeURIComponent(window.location.origin + '/embed/v1.0/authentication/google/return')}`, { method: 'GET' });
    const { jwt } = response as any;
    setToken(jwt);
    if (additionalParams?.redirectToRegisterWithCompletionState) {
        const completionState = await getUserCompletionState();
        if (!hasCompletedRegistration(completionState)) {
            additionalParams.redirectToRegisterWithCompletionState(completionState, additionalParams.search)
        } else if (Boolean(additionalParams.goToRedirectUrl)) {
            additionalParams.goToRedirectUrl?.()
        } else if (Boolean(additionalParams.redirectToGroups)) {
            additionalParams.redirectToGroups?.()
        }
    }
    return jwt;
};

export const getCurrentUser = async (): Promise<Partial<User> | undefined> => {
    const currentToken = getToken();
    if (currentToken) {
        const tokenHeaders = getTokenRequestHeaders(currentToken);
        const { new_token, ...user } = await qidigoFetch(`whoami`, { method: 'GET', headers: tokenHeaders });
        if (new_token) {
            setToken(new_token);
        }
        return user;
    }
    return undefined
};

export const getUserCompletionState = async (): Promise<UserCompletionState> => {
    const user = await getCurrentUser();
    if (!!user && !!user.id) {
        const userWithBirthday = await qidigoAuthenticatedFetch(`users/${user.id}`, { method: 'GET' });
        const addresses = await qidigoAuthenticatedFetch(`users/${user.id}/addresses`, { method: 'GET' });
        const phoneNumbers = await qidigoAuthenticatedFetch(`users/${user.id}/phone_numbers`, { method: 'GET' });
        return {
            email: true,
            birthday: !!userWithBirthday.birthday,
            address: addresses.length > 0,
            phoneNumber: phoneNumbers.length > 0
        };
    } else return { email: false, birthday: false, address: false, phoneNumber: false };
};

interface RequestAddress {
    civicNumberAndStreet: string,
    postalCode: string,
    countryId: string,
    state: string,
    city: string,
    cityId?: number,
    civicNumberExt?: string
}

export const newAddress = async (userId: number, requestAddress: RequestAddress, additionalParams?: RequestAdditionalParams) => {
    const adressesInit = {
        method: 'POST',
        body: JSON.stringify({
            address: {
                civic_number_and_street: requestAddress.civicNumberAndStreet,
                civic_number_ext: !!requestAddress.civicNumberExt ? requestAddress.civicNumberExt : undefined,
                postal_code: requestAddress.postalCode,
                country_id: requestAddress.countryId,
                state_id: requestAddress.state,
                city_id: requestAddress.cityId,
            }
        })
    };
    return qidigoAuthenticatedFetch(`users/${userId}/addresses`, adressesInit, additionalParams);
};

export const newPhoneNumber = async (userId: number, phoneNumber: string, additionalParams?: RequestAdditionalParams) => {
    const phoneNumberInit = {
        method: 'POST',
        body: JSON.stringify({
            phone_number: {
                ext: '',
                number: phoneNumber,
                country_id: 'CA'
            }
        })
    };
    return qidigoAuthenticatedFetch(`users/${userId}/phone_numbers`, phoneNumberInit, additionalParams);
};

interface RequestFamilyMember {
    firstName: string,
    lastName: string,
    birthday: string,
    sex: string
}

export const newFamilyMember = async (userId: number, requestFamilyMember: RequestFamilyMember, additionalParams?: RequestAdditionalParams): Promise<FamilyMember> => {
    const familyMemberInit = {
        method: 'POST',
        body: JSON.stringify({
            family_member: {
                first_name: requestFamilyMember.firstName,
                last_name: requestFamilyMember.lastName,
                birthday: requestFamilyMember.birthday,
                sex: requestFamilyMember.sex,
            }
        })
    };
    return qidigoAuthenticatedFetch(`users/${userId}/family_members`, familyMemberInit, additionalParams);
};

export const getGroupsWithOpenSessionPlansForActivity = async (activityId: number, additionalParams?: RequestAdditionalParams): Promise<Group[]> => {
    const groupObject = await qidigoFetch(`activities/${activityId}/groups?type_plan=SESSION`, { method: 'GET' }, additionalParams);
    return groupObject.groups || [];
};

export const getRelatedOffers = async (organizationId: number, planIds: number[], additionalParams?: RequestAdditionalParams): Promise<{
    related_offers: IRelatedOffer[]
}> => {
    const queryString = 'plan_ids[]=' + planIds.join('&plan_ids[]=')
    return await qidigoFetch(`organizations/${organizationId}/related_offers?${queryString}`, { method: 'GET' }, additionalParams);
};

export const getAllActivities = async (
    { organizationId }: { organizationId: number },
    additionalParams?: RequestAdditionalParams
): Promise<{ activities: IActivity[] }> => {
    return await qidigoFetch(`organizations/${organizationId}/activities`, { method: 'GET' }, additionalParams);
};

export const getGroupsByPrograms = async (
    { organizationId, programIds }: { organizationId: number, programIds: number[] },
    additionalParams?: RequestAdditionalParams
): Promise<{ groups: ICalendarGroup[] }> => {
    const programIdsSearch = 'program_ids[]=' + programIds.join('&program_ids[]=')

    return await qidigoFetch(`organizations/${organizationId}/programs/groups?${programIdsSearch}`, { method: 'GET' }, additionalParams);
};


export const getGroup = async (groupId: number, additionalParams?: RequestAdditionalParams): Promise<Group> => {
    const groupObject = await qidigoFetch(`groups/${groupId}`, { method: 'GET' }, additionalParams);
    return groupObject.group || [];
};

type BatchGroupOffers = {
  groups: GroupOffer[],
  familyMembers?: {
    [key: string]: Partial<FamilyMember>
  }
}

type GroupOffer = {
  id: number,
  offers: Offer[],
}

type Offers = {
    offers: Offer[],
    familyMembers?: {
        [key: string]: Partial<FamilyMember>
    }
}
export const getOffersForGroup = async (groupId: number, additionalParams?: RequestAdditionalParams): Promise<Offers> => {
    const offersObject = await qidigoAuthenticatedFetch(`groups/${groupId}/offers`, { method: 'GET' }, additionalParams);
    return { offers: offersObject.offers, familyMembers: offersObject._bundled && offersObject._bundled.family_members };
};
export const getSessionOffersForGroup = async (groupId: number, additionalParams?: RequestAdditionalParams): Promise<Offers> => {
    const offersObject = await qidigoAuthenticatedFetch(`groups/${groupId}/offers`, { method: 'GET' }, additionalParams);
    const sessionOffers = offersObject.offers.filter((offer: { plan: { type_plan: string; }; }) => offer.plan.type_plan === 'SESSION');
    return { offers: sessionOffers, familyMembers: offersObject._bundled && offersObject._bundled.family_members };
};
export const getSessionOffersForGroups = async (groupIds: number[], organizationId: number, additionalParams?: RequestAdditionalParams): Promise<BatchGroupOffers> => {
  const queryString = `organization_id=${organizationId}` + (groupIds.length > 0 ? '&group_ids[]=' + groupIds.join('&group_ids[]=') : '');
  const batchGroupSessionOffersObject = await qidigoAuthenticatedFetch(`batch-group-session-offers?${queryString}`, { method: 'GET' }, additionalParams);
  return { groups: batchGroupSessionOffersObject.groups, familyMembers: batchGroupSessionOffersObject._bundled && batchGroupSessionOffersObject._bundled.family_members };
};
export const getWishesByOrganizationId = async (organizationId: number, additionalParams?: RequestAdditionalParams): Promise<{
    wishes: Wish[]
}> => {
    const response = await qidigoFetch(`organizations/${organizationId}/wishes?session_uuid=${getOrInitGuestToken()}`,
        { method: 'GET' }, additionalParams
    );

    return response;
};

export const getMembershipsByOrganizationId = async (organizationId: number, additionalParams?: RequestAdditionalParams): Promise<{
    membership_templates: Membership[]
}> => {
    return await qidigoFetch(`organizations/${organizationId}/memberships`, { method: 'GET' }, additionalParams)
};

export const getMembershipTemplateOffers = async (membershipTemplateId: number, additionalParams?: RequestAdditionalParams): Promise<{
    offers: Offer[],
    membership_template: Membership,
    familyMembers?: {
        [key: string]: Partial<FamilyMember>
    }
}> => {
    const offersObject = await qidigoAuthenticatedFetch(`membership_templates/${membershipTemplateId}/offers`, { method: 'GET' }, additionalParams)
    return {
        offers: offersObject.offers,
        membership_template: offersObject.membership_template,
        familyMembers: offersObject._bundled && offersObject._bundled.family_members
    }
}

export const deleteWishFromWishlist = async (organizationId: number, wishId: number, additionalParams?: RequestAdditionalParams): Promise<{
    wishes: Wish[]
}> => {
    await qidigoFetch(`organizations/${organizationId}/wishes/${wishId}?session_uuid=${getOrInitGuestToken()}`, { method: 'DELETE' }, additionalParams)
    return await getWishesByOrganizationId(organizationId);
};

export const deleteAllWishesForOrganization = async (organizationId: number, additionalParams?: RequestAdditionalParams): Promise<any> => {
    return await qidigoFetch(`organizations/${organizationId}/flush_wishes?session_uuid=${getOrInitGuestToken()}`, { method: 'DELETE' }, additionalParams)
};

export const updateWishFromWishlist = async (organizationId: number, wishId: number, payload: {
    memberId: number | null,
    planId: number | null
}, additionalParams?: RequestAdditionalParams): Promise<{
    wishes: Wish[]
}> => {
    await qidigoFetch(
        `organizations/${organizationId}/wishes/${wishId}`,
        {
            method: 'PATCH',
            body: JSON.stringify({
                session_uuid: getOrInitGuestToken(),
                subscriber_user_id: payload.memberId,
                plan_id: payload.planId
            })
        },
        additionalParams
    )
    return await getWishesByOrganizationId(organizationId);
};

interface ICartResponse {
    items: CartItem[],
    count: number,
    cart_expiration: string
}

export const getCartItems = async (additionalParams?: RequestAdditionalParams): Promise<ICartResponse> => {
    const {organizationId, ...rest} = additionalParams || {};
    const response: ICartResponse = environment.REACT_APP_IS_DEBUG
        ? {
            items: [],
            count: 0,
            cart_expiration: 0
        }
        : await qidigoAuthenticatedFetch(`cart`, {method: 'GET'}, {...rest})
    response.items = response.items.filter((it) => {
        if (!!organizationId && !!it.organization) return it.organization.id === organizationId;
        return true;
    });
    const itemIds = response.items.map((item) => item.id);
    setItems(itemIds);
    return response;
};

export const addToWishlist = async (organizationId: number,
                                    group_ids?: Array<number>,
                                    activity_id?: number,
                                    program_id?: number,
                                    userId?: number
): Promise<BasicPostResponse> => {
    const addToWishlistInit = {
        method: 'POST',
        body: JSON.stringify({
            session_uuid: getOrInitGuestToken(),
            group_ids: group_ids,
            activity_id: activity_id,
            program_id: program_id,
            user_id: userId
        })
    };
    return await qidigoFetch(`organizations/${organizationId}/wishes`, addToWishlistInit);
};
export const setGuestToken = (token: string): void => {
    localStorage.setItem(QIDIGO_GUEST_TOKEN, token);
}

export const getOrInitGuestToken = () : string => {
    let guestToken = localStorage.getItem(QIDIGO_GUEST_TOKEN);

    if (guestToken === null) {
        guestToken = uuidv4();
        setGuestToken(guestToken);
    }

    return guestToken;
}

export interface AddToCartResponse extends BasicPostResponse {
    groups?: number[];
    subscriber?: number;
    cart_item?: number
    restrictions?: (string | IRestrictionByMembership)[]
}

export const addToCart = async (userId: number, planId: number, additionalParams?: RequestAdditionalParams): Promise<AddToCartResponse> => {
    trackEvent(
        'add_to_cart',
        {
            label: planId
        }
    );
    const cartInit = {
        method: 'POST',
        body: JSON.stringify({
            cart_item: {
                quantity: 1,
                subscriber_user_id: userId,
                plan_id: planId,
            }
        })
    };
    const response = await qidigoAuthenticatedFetch(`cart`, cartInit, additionalParams);
    if (response.cart_item) addItem(response.cart_item);
    return response;
};

export const deleteCartItem = async (itemId: number, additionalParams?: RequestAdditionalParams): Promise<BasicPostResponse> => {
    trackEvent(
        'remove_from_cart',
        {
            label: itemId
        }
    );
    const response = await qidigoAuthenticatedFetch(`cart/${itemId}`, { method: 'DELETE' }, additionalParams);
    removeItem(itemId);
    return response;
};

export interface IFetchLocationsResponse {
    locations: ILocation[]
}

export const getLocations = async (
    {
        organizationId,
        selectionableRentables
    }: {
        organizationId: number,
        selectionableRentables: string[]
    },
    additionalParams?: RequestAdditionalParams
): Promise<IFetchLocationsResponse> => {
    if (environment.REACT_APP_IS_DEBUG) {
        return {
            "locations": [
                {
                    "id": 1,
                    "name": "Chalet «Le beau hibou»",
                    "facilities": [
                        {
                            "id": 1,
                            "name": "Ferme Chalet entier",
                            "slug": "ferme-chalet-entier"
                        }
                    ]
                },
                {
                    "id": 2,
                    "name": "Centre sportif",
                    "facilities": [
                        {
                            "id": 40,
                            "name": "Ferme",
                            "slug": "ferme"
                        },
                        {
                            "id": 3,
                            "name": "Écurie Gymnase",
                            "slug": "ecurie-gymnase",
                            "units": [
                                {
                                    "id": 1,
                                    "name": "Plateau 1 (Badminton)",
                                    "slug": "plateau1-badminton"
                                },
                                {
                                    "id": 4,
                                    "name": "La petite ferme",
                                    "slug": "la-petite-ferme"
                                },
                                {
                                    "id": 5,
                                    "name": "Écurie",
                                    "slug": "ecurie"
                                },
                                {
                                    "id": 6,
                                    "name": "10",
                                    "slug": "10"
                                },
                                {
                                    "id": 7,
                                    "name": "1",
                                    "slug": "1"
                                },
                                {
                                    "id": 8,
                                    "name": "2",
                                    "slug": "2"
                                },
                                {
                                    "id": 9,
                                    "name": "20",
                                    "slug": "20"
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    }
    const urlParams = new URLSearchParams()
    selectionableRentables.forEach((x, i) => {
        urlParams.set(`selected_rentables[${i}]`, x)
    })

    return await qidigoFetch(`organizations/${organizationId}/iframe/locations?${urlParams.toString()}`, { method: 'GET' }, additionalParams);
};

export const getRentablesUnavailabilities = async (
    {
        organizationId,
        units,
        facilities,
        startDate,
        endDate
    }: {
        organizationId: number,
        units: string[],
        facilities: string[],
        startDate: string,
        endDate: string
    },
    additionalParams?: RequestAdditionalParams
): Promise<IRentableUnavailability[]> => {
    if (environment.REACT_APP_IS_DEBUG) {
        return [
            {
                "id": 1,
                "type": "facility",
                "name": "Chalet entier",
                "unavailabilities": [
                    {
                        "id": 1,
                        "type": "facility",
                        "date": "2022-01-17T00:00:00.000Z",
                        "start_time": "8:00",
                        "end_time": "11:00"
                    },
                    {
                        "id": 1,
                        "type": "facility",
                        "date": "2022-01-19T00:00:00.000Z",
                        "start_time": "11:00",
                        "end_time": "12:00"
                    },
                    {
                        "id": 1,
                        "type": "facility",
                        "date": "2022-01-19T00:00:00.000Z",
                        "start_time": "13:00",
                        "end_time": "15:00"
                    },
                    {
                        "id": 1,
                        "type": "facility",
                        "date": "2022-01-20T00:00:00.000Z",
                        "start_time": "15:00",
                        "end_time": "20:00"
                    },
                    {
                        "id": 1,
                        "type": "facility",
                        "date": "2022-01-22T00:00:00.000Z",
                        "start_time": "8:00",
                        "end_time": "21:00"
                    }
                ]
            },
            {
                "id": 2,
                "type": "unit",
                "name": "Plateau 1 - 3 (tennis, volleyball)",
                "unavailabilities": [
                    {
                        "id": 2,
                        "type": "unit",
                        "date": "2022-01-17T00:00:00.000Z",
                        "start_time": "10:00",
                        "end_time": "21:00"
                    },
                    {
                        "id": 2,
                        "type": "unit",
                        "date": "2022-01-19T00:00:00.000Z",
                        "start_time": "11:00",
                        "end_time": "15:00"
                    },
                    {
                        "id": 5,
                        "type": "unit",
                        "date": "2022-01-20T00:00:00.000Z",
                        "start_time": "15:00",
                        "end_time": "20:00"
                    },
                    {
                        "id": 4,
                        "type": "unit",
                        "date": "2022-01-21T00:00:00.000Z",
                        "start_time": "13:00",
                        "end_time": "14:00"
                    },
                    {
                        "id": 4,
                        "type": "unit",
                        "date": "2022-01-22T00:00:00.000Z",
                        "start_time": "8:00",
                        "end_time": "13:00"
                    },
                    {
                        "id": 2,
                        "type": "facility",
                        "date": "2022-01-22T00:00:00.000Z",
                        "start_time": "8:00",
                        "end_time": "13:00"
                    }
                ]
            }
        ]
    }
    const urlParams = new URLSearchParams()
    if (units.length > 0) {
        units.forEach((x, i) => {
            urlParams.set(`facilities_or_units[${i}][type]`, "unit")
            urlParams.set(`facilities_or_units[${i}][id]`, x)
        })
    }
    if (facilities.length > 0) {
        facilities.forEach((x, i) => {
            urlParams.set(`facilities_or_units[${i + units.length}][type]`, "facility")
            urlParams.set(`facilities_or_units[${i + units.length}][id]`, x)
        })
    }
    urlParams.append('starting_at', startDate)
    urlParams.append('ending_at', endDate)

    return await qidigoFetch(`organizations/${organizationId}/iframe/locations/unavailabilities?${urlParams.toString()}`, { method: 'GET' }, additionalParams);
};

export const newContact = async (organizationId: number, userId: number): Promise<BasicPostResponse> => {
    const newContactInit = {
        method: 'POST',
        body: JSON.stringify({
            organization_id: organizationId,
            user_id: userId
        })
    };

    return await qidigoAuthenticatedFetch(`contacts`, newContactInit);
};

export const getCssByOrganizationId = async (organizationId: number, additionalParams?: RequestAdditionalParams): Promise<string> => {
    const response = await qidigoFetch(`organizations/${organizationId}/iframe/css`, {method: 'GET'}, additionalParams)

    return response.css;
};

export interface ICustomLabelResponse {
    [label: string]: string;
}

export const getCustomLabelByOrganizationSlug = async (
    organizationSlug: string,
    requestedCustomLabel: string[],
    additionalParams?: RequestAdditionalParams
): Promise<ICustomLabelResponse> => {
    const customLabelsInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestedCustomLabel),
    };

    const rawCustomLabels = await qidigoFetch(`custom-labels/${organizationSlug}`, customLabelsInit, additionalParams);

    return JSON.parse(rawCustomLabels);
};
